import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';

/**
 * M-4: send the session cookie with every API call.
 *
 * The access token now travels as an httpOnly cookie (`xv_at`), which the
 * browser only attaches when the request is made with credentials. Angular's
 * HttpClient defaults to withCredentials: false, so without this every call
 * would arrive unauthenticated once the server stops accepting the copy that
 * used to be kept in localStorage.
 *
 * The token in localStorage is now signature-stripped - `header.payload.` - so
 * NbAuthJWTInterceptor still attaches it as a bearer header and the route
 * guards can still read the role and expiry from it, but it is no longer a
 * usable credential on its own. The cookie is what actually authenticates.
 */
/** Where the /apply wizard keeps its apply token. Per-tab, cleared on close. */
export const APPLY_TOKEN_KEY = 'xv_apply_token';

export function readApplyToken(): string {
  try {
    return sessionStorage.getItem(APPLY_TOKEN_KEY) || '';
  } catch (e) {
    // Private mode / storage blocked. No token is a normal state.
    return '';
  }
}

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    /*
     * /apply is the registration page, so those calls have no session behind
     * them. addFirstFormData issues a signed apply token bound to the applicant
     * it created; the server reads the applicant id out of it rather than
     * trusting a user_id in the query string. Sent whenever one is present -
     * the server ignores it for routes that do not ask for it.
     */
    const applyToken = readApplyToken();

    const authorized = applyToken
      ? req.clone({ withCredentials: true, setHeaders: { 'X-Apply-Token': applyToken } })
      : req.clone({ withCredentials: true });

    return next.handle(authorized);
  }
}
